import { getClientRegion } from "./region";
import {
  type SiteSettings,
  settingsEndpoint,
  siteSettingsTemplateId,
  CLIENT_CACHE_TTL,
  normalizeSiteSettings
} from "./settingsShared";

declare global {
  interface Window {
    __SITE_SETTINGS__?: SiteSettings;
    __SITE_SETTINGS_LOCALE__?: string;
    __SITE_SETTINGS_REGION__?: string | null;
  }
}

let lastClientFetchTime = 0;
let refreshPromise: Promise<SiteSettings | null> | null = null;
let refreshPromiseLocale = "";
// Tracked alongside the locale because the cache was previously keyed on locale
// alone: proxy.ts REWRITES /eg/en to /en, so both regions are the same Next
// route and the layout is reused across a region switch. Without this the
// browser kept serving the previous region's palette and logo until the TTL
// expired — the region-switch-keeps-the-old-style bug.
let refreshPromiseRegion: string | null = null;

export async function refreshClientSettings(
  locale = "en",
  regionOverride?: string | null,
): Promise<SiteSettings | null> {
  if (typeof window === "undefined") return null;

  const region =
    regionOverride !== undefined ? regionOverride : getClientRegion(window.location.pathname);

  // If there is already a promise fetching the exact same locale AND region,
  // return it. Otherwise start a new request, so we never resolve a region
  // switch with the previous region's payload.
  if (refreshPromise && refreshPromiseLocale === locale && refreshPromiseRegion === region) {
    return refreshPromise;
  }

  refreshPromiseLocale = locale;
  refreshPromiseRegion = region;

  refreshPromise = (async () => {
    try {
      let url = `${settingsEndpoint}?locale=${encodeURIComponent(locale)}`;
      if (region) {
        url += `&region_slug=${encodeURIComponent(region)}`;
      }
      const response = await fetch(
        url,
        {
          headers: { Accept: "application/json" },
        }
      );

      if (!response.ok) {
        console.warn(`[refreshClientSettings] API error: ${response.status}`);
        return null;
      }

      const data: unknown = await response.json();
      const settings = normalizeSiteSettings(data);

      // Only apply settings if they still match the active locale AND region —
      // a slow response for the region the user just navigated away from must
      // not overwrite the one they are now on.
      if (settings && refreshPromiseLocale === locale && refreshPromiseRegion === region) {
        console.log(`[refreshClientSettings] New settings applied for locale "${locale}", region "${region ?? "global"}": ${settings.site.name}`);
        window.__SITE_SETTINGS__ = settings;
        window.__SITE_SETTINGS_LOCALE__ = locale;
        window.__SITE_SETTINGS_REGION__ = region;
        lastClientFetchTime = Date.now();
        window.dispatchEvent(new CustomEvent("site-settings-updated", { detail: settings }));
      }

      return settings;
    } catch {
      return null;
    } finally {
      if (refreshPromiseLocale === locale && refreshPromiseRegion === region) {
        refreshPromise = null;
      }
    }
  })();

  return refreshPromise;
}

export function readCachedSiteSettings(
  locale = "en",
  regionOverride?: string | null,
): SiteSettings | null {
  if (typeof window === "undefined") {
    return null;
  }

  const now = Date.now();
  const cachedLocale = window.__SITE_SETTINGS_LOCALE__;
  const cachedRegion = window.__SITE_SETTINGS_REGION__;
  const region =
    regionOverride !== undefined ? regionOverride : getClientRegion(window.location.pathname);

  // If this is the initial load (cachedLocale is undefined), we read from the script tag.
  // Otherwise, check if the locale or the region has changed.
  const isInitialLoad = cachedLocale === undefined;
  const hasLocaleMismatch = !isInitialLoad && cachedLocale !== locale;
  const hasRegionMismatch = cachedRegion !== undefined && cachedRegion !== region;
  const hasMismatch = hasLocaleMismatch || hasRegionMismatch;
  const isStale = hasMismatch || (now - lastClientFetchTime > CLIENT_CACHE_TTL);

  // If we have settings, they are not stale, and the locale matches, return them.
  if (window.__SITE_SETTINGS__ && !isStale) {
    return window.__SITE_SETTINGS__;
  }

  // On a locale OR region mismatch, don't read the script tag in document.head:
  // the root layout is preserved during client-side navigation, so that tag
  // still holds the settings of the language/region we just left.
  if (hasMismatch) {
    void refreshClientSettings(locale, region);
    return null;
  }

  const template = document.getElementById(siteSettingsTemplateId);
  
  if (!(template instanceof HTMLScriptElement)) {
    if (window.__SITE_SETTINGS__) {
      if (isStale) void refreshClientSettings(locale, region);
      return window.__SITE_SETTINGS__;
    }
    return null;
  }

  const serializedSettings = template.textContent?.trim();

  if (!serializedSettings) {
    if (window.__SITE_SETTINGS__ && isStale) void refreshClientSettings(locale, region);
    return window.__SITE_SETTINGS__ ?? null;
  }

  try {
    const settings = JSON.parse(serializedSettings) as SiteSettings;

    if (settings && typeof settings === "object") {
      window.__SITE_SETTINGS__ = settings;
      window.__SITE_SETTINGS_LOCALE__ = locale;
      window.__SITE_SETTINGS_REGION__ = region;
      lastClientFetchTime = now;

      // Subscribers (useSiteSettings) learn about cache changes only through
      // this event. Without it, a consumer rendered outside the provider — with
      // no server seed to start from — would read the script tag into the cache
      // and then never re-render to show it.
      window.dispatchEvent(new CustomEvent("site-settings-updated", { detail: settings }));
    }

    if (isStale) {
      void refreshClientSettings(locale, region);
    }

    return settings;
  } catch {
    if (window.__SITE_SETTINGS__ && isStale) void refreshClientSettings(locale, region);
    return window.__SITE_SETTINGS__ ?? null;
  }
}
